import React, { useEffect, useState } from "react";
import axios from "axios";
import Swal from "sweetalert2";
import { Button, DatePicker, Input, Select, Table } from "antd";
import config from "../../../config";
import authHeader from "../../Authentication/authHeader";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";

export default function CorporateLedgerReport({ tabChange }) {
  dayjs.extend(utc);
  dayjs.extend(timezone);
  const TIMEZONE = "Asia/Dhaka";
  const { Option } = Select;
  const { Search } = Input;
  const { RangePicker } = DatePicker;
  const [supplierLedgers, setSupplierLedgers] = useState([]);
  const [salesLoading, setSalesLoading] = useState(false);
  const [selectedDateRange, setSelectedDateRange] = useState([]);
  const [search, setSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);

  const handleDateRange = (value) => {
    setCurrentPage(1);
    if (Array.isArray(value) && value.length === 2 && value[0] && value[1]) {
      let startDate = dayjs.tz(value[0], TIMEZONE).startOf("day");
      let endDate = dayjs.tz(value[1], TIMEZONE).endOf("day");

      // ✅ Store ISO strings with timezone offset
      setSelectedDateRange([
        startDate.format("YYYY-MM-DDTHH:mm:ssZ"), // e.g. 2025-09-21T00:00:00+06:00
        endDate.format("YYYY-MM-DDTHH:mm:ssZ"),
      ]);
    } else {
      setSelectedDateRange(null);
    }
  };
  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const fetchData = async () => {
    try {
      setSalesLoading(true);
      const response = await axios.get(
        `${config.baseUrl}/inventory/customer_ledger_list/`,
        {
          headers: authHeader(),
          params: {
            dateRange: selectedDateRange,
            search: search,
            exclude_corporate: false,
            page: currentPage,
          },
        }
      );
      const resData = Array.isArray(response?.data?.results)
        ? response?.data?.results
        : [];
      setTotalItems(response?.data?.count || 0);
      setSupplierLedgers(resData);
    } catch (error) {
      Swal.fire({
        title: "Error",
        text: error,
        icon: "error",
        confirmButtonText: "OK",
      });
    } finally {
      setSalesLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [selectedDateRange, search, tabChange, currentPage]);

  const supplierLedgerColumns = [
    {
      title: "Date",
      render: (record) => record?.date,
    },
    {
      title: "Customer Type",
      dataIndex: "customer_type",
    },
    {
      title: "Customer",
      render: (record) =>
        record?.farmer ||
        record?.retailer ||
        record?.customer_name ||
        record?.corporate_customer_name,
    },
    {
      title: "Type",
      dataIndex: "voucher_type",
    },
    {
      title: "Particulars",
      dataIndex: "particulars",
    },

    {
      title: "Dr",
      render: (record) =>
        Number(record?.debit?.toFixed(2)).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
    },
    {
      title: "Cr",
      render: (record) =>
        Number(record?.credit?.toFixed(2)).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        }),
    },
    {
      title: "Balance",
      render: (record) => {
        if (
          record?.calculated_balance === undefined ||
          record?.calculated_balance === null
        ) {
          return "0.00";
        }

        const balance = Number(record.calculated_balance.toFixed(2));
        const formatted = Math.abs(balance).toLocaleString(undefined, {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });

        return balance >= 0 ? `${formatted} Dr` : `${formatted} Cr`;
      },
    },
  ];

  return (
    <>
      <div className="farmerDashboard">
        <div className="shadow-sm p-3">
          <div className="m-2 mt-4 d-flex justify-content-between align-items-center">
            <div>
              <RangePicker
                onChange={handleDateRange}
                value={
                  selectedDateRange
                    ? selectedDateRange.map((date) => dayjs(date))
                    : null
                }
                style={{ width: "320px" }}
              />
            </div>
          </div>
          <div className="m-2 mt-4 d-flex justify-content-start align-items-center">
            <Search
              className="w-25"
              placeholder="search corporate / particulars here..."
              allowClear
              onSearch={(value) => setSearch(value)}
              enterButton={
                <Button
                  style={{
                    backgroundColor: "#46604A",
                    borderColor: "#46604A",
                    color: "white",
                  }}
                >
                  Search
                </Button>
              }
            />
          </div>
          <Table
            columns={supplierLedgerColumns}
            dataSource={supplierLedgers}
            loading={salesLoading}
            rowKey={(record) => record?.pl_id}
            size="small"
            pagination={{
              current: currentPage,
              pageSize: 10,
              total: totalItems,
              onChange: handlePageChange,
            }}
          />
        </div>
      </div>
    </>
  );
}
